


import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageProvider";
import { QuestsIcon, TrophyIcon } from "../components/icons";
import CopyrightFooter from "../components/CopyrightFooter";
import { FaInstagram, FaTiktok } from "react-icons/fa";
import { FiCheckCircle, FiMonitor, FiSmartphone, FiXCircle } from "react-icons/fi";

const HOW_IT_WORKS_STEPS = [
  {
    start: 0,
    de: {
      title: "Turnier auswählen",
      text: "Suche ein für dich passendes Turnier aus und melde dich an.",
    },
    en: {
      title: "Choose a tournament",
      text: "Find a tournament that suits you and sign up.",
    },
  },
  {
    start: 2.63,
    de: {
      title: "Turnierlobby",
      text: "Nun bist du in der Lobby und wartest auf den Turnierstart.",
    },
    en: {
      title: "Tournament lobby",
      text: "You are now in the lobby and waiting for the tournament to start.",
    },
  },
  {
    start: 10.73,
    de: {
      title: "Turnierstart",
      text: "Nachdem dir dein Gegner zugelost wurde, kannst du dein erstes Spiel spielen. Die EA-ID deines Gegners findest du in seinem Profil.",
    },
    en: {
      title: "Tournament start",
      text: "Once your opponent has been assigned, you can play your first match. You can find your opponent's EA ID in their profile.",
    },
  },
  {
    start: 13.83,
    de: {
      title: "Ergebnis melden",
      text: "Wenn dein Match vorbei ist, kannst du zwischen mehreren Reportoptionen auswählen oder das gemeldete Ergebnis deines Gegners bestätigen.",
    },
    en: {
      title: "Report result",
      text: "When your match is over, you can choose between several reporting options or confirm the result reported by your opponent.",
    },
  },
  {
    start: 17.9,
    de: {
      title: "Konfliktfälle",
      text: "Achte darauf, wahrheitsgemäße Angaben zu machen, da andernfalls ein Konfliktfall erstellt wird.",
    },
    en: {
      title: "Conflict cases",
      text: "Make sure your information is truthful, otherwise a conflict case will be created.",
    },
  },
  {
    start: 20.53,
    de: {
      title: "Evidence-Upload",
      text: "Im Rahmen eines Konfliktfalls kannst du bis zu drei Bilder hochladen, die dein gemeldetes Ergebnis belegen sollen. Achte darauf, dass auf den Bildern eure EA-ID sowie das Ergebnis gut erkennbar sind.",
    },
    en: {
      title: "Evidence upload",
      text: "During a conflict case, you can upload up to three images to support your reported result. Make sure your EA ID and the result are clearly visible.",
    },
  },
  {
    start: 26.95,
    de: {
      title: "Konfliktentscheidung",
      text: "Auf Grundlage der hochgeladenen Bilder entscheidet ein Admin über das Ausscheiden oder Weiterkommen der beteiligten Spieler.",
    },
    en: {
      title: "Conflict decision",
      text: "Based on the uploaded images, an admin decides whether the involved players advance or are eliminated.",
    },
  },
  {
    start: 29.45,
    de: {
      title: "Turniersieg",
      text: "Solltest du wider Erwarten ein Turnier gewinnen, erhältst du deinen Gutschein sowohl über das VaultQuest-Benachrichtigungssystem als auch zusätzlich an die bei uns hinterlegte E-Mail-Adresse.",
    },
    en: {
      title: "Tournament victory",
      text: "If you win a tournament, you will receive your voucher both through the VaultQuest notification system and via the email address stored with us.",
    },
  },
];

const LANDING_BACKGROUND = "linear-gradient(180deg, #0f0418 0%, #1a0930 30%, #ff007a 120%)";

function getHowItWorksStepIndex(currentTime) {
  for (let index = HOW_IT_WORKS_STEPS.length - 1; index >= 0; index -= 1) {
    if (currentTime >= HOW_IT_WORKS_STEPS[index].start) {
      return index;
    }
  }

  return 0;
}


export default function LandingPage() {
   const { t, language, setLanguage } = useLanguage(); 
  const navigate = useNavigate();
  const [activeHowItWorksIndex, setActiveHowItWorksIndex] = useState(0);
  const activeHowItWorksIndexRef = useRef(0);

  const openLandingInfoPage = useCallback(
    (path) => {
      navigate(path, { state: { from: "landing" } });
    },
    [navigate]
  );

  const activeHowItWorksStep = useMemo(
    () => HOW_IT_WORKS_STEPS[activeHowItWorksIndex],
    [activeHowItWorksIndex]
  );

  const activeHowItWorksCopy =
    activeHowItWorksStep[language] || activeHowItWorksStep.de;

  const updateHowItWorksStep = useCallback((currentTime) => {
    const nextIndex = getHowItWorksStepIndex(currentTime);

    if (nextIndex !== activeHowItWorksIndexRef.current) {
      activeHowItWorksIndexRef.current = nextIndex;
      setActiveHowItWorksIndex(nextIndex);
    }
  }, []);

  const handleHowItWorksTimeUpdate = useCallback((event) => {
    updateHowItWorksStep(event.currentTarget.currentTime || 0);
  }, [updateHowItWorksStep]);

  const handleHowItWorksLoopReset = useCallback(() => {
    activeHowItWorksIndexRef.current = 0;
    setActiveHowItWorksIndex(0);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousBodyBackground = document.body.style.background;
    const previousHtmlBackground = document.documentElement.style.background;
    const previousRootBackground = document.getElementById("root")?.style.background;
    const root = document.getElementById("root");

    document.documentElement.style.background = LANDING_BACKGROUND;
    document.body.style.background = LANDING_BACKGROUND;
    if (root) root.style.background = LANDING_BACKGROUND;

    return () => {
      document.documentElement.style.background = previousHtmlBackground;
      document.body.style.background = previousBodyBackground;
      if (root) root.style.background = previousRootBackground || "";
    };
  }, []);

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes howItWorksCopyIn {
            from {
              opacity: 0;
              transform: translate3d(0, 10px, 0);
            }
            to {
              opacity: 1;
              transform: translate3d(0, 0, 0);
            }
          }

          .how-it-works-copy {
            animation: howItWorksCopyIn 300ms ease both;
          }

          @media (max-width: 820px) {
            .how-it-works-row {
              grid-template-columns: 1fr !important;
              gap: 22px !important;
              padding: 22px !important;
            }

            .how-it-works-video-shell {
              order: 2;
            }

            .landing-info-grid {
              grid-template-columns: 1fr !important;
              gap: 28px !important;
            }
          }
        `}
      </style>
      <div style={styles.pageGlow} />
	       <div style={styles.languageSwitch}>
  <button
    style={{
      ...styles.languageBtn,
      ...(language === "de" ? styles.languageBtnActive : {}),
    }}
    onClick={() => setLanguage("de")}
    aria-label="Deutsch"
    title="Deutsch"
  >
    🇩🇪
  </button>

  <button
    style={{
      ...styles.languageBtn,
      ...(language === "en" ? styles.languageBtnActive : {}),
    }}
    onClick={() => setLanguage("en")}
    aria-label="English"
    title="English"
  >
    🇬🇧
  </button>
</div>
      {/* HERO */}
      <section style={styles.hero}>
        <div style={styles.heroGlow} />
        <img
          src="/vaultquest-logo.png"
          alt="VaultQuest"
          style={styles.heroLogo}
        />
        <h1 style={styles.title}>
  {t("landing.heroTitle")}
</h1>
        <p style={styles.heroClaim}>
  {t("landing.heroClaim")}
</p>

        <div style={styles.buttons}>
  <button style={styles.primaryBtn} onClick={() => navigate("/login")}>
  {t("landing.startButton")}
</button>
</div>

<div style={styles.socialRow}>
  <a
    href="https://instagram.com/vaultquestofficial"
    target="_blank"
    rel="noopener noreferrer"
    style={styles.socialIconBtn}
  >
    <FaInstagram />
  </a>

  <a
    href="https://www.tiktok.com/@vaultquest_de"
    target="_blank"
    rel="noopener noreferrer"
    style={styles.socialIconBtn}
  >
    <FaTiktok />
  </a>
</div>

<div className="landing-info-grid" style={styles.infoGrid}>
  <div style={styles.infoGroup}>
    <div style={styles.infoHeading}>
      <FiSmartphone aria-hidden="true" />
      <span>{t("landing.optimizedForTitle")}</span>
    </div>
    <div style={styles.infoRows}>
      <div style={styles.infoRow}>
        <FiCheckCircle style={styles.infoPositiveIcon} aria-hidden="true" />
        <span>{t("landing.optimizedSmartphones")}</span>
      </div>
    </div>
    <p style={styles.infoFinePrint}>
      {t("landing.optimizedForText")}
    </p>
  </div>

  <div style={styles.infoGroup}>
    <div style={styles.infoHeading}>
      <FiMonitor aria-hidden="true" />
      <span>{t("landing.supportedPlatformsTitle")}</span>
    </div>
    <div style={styles.infoRows}>
      <div style={styles.infoRow}>
        <FiCheckCircle style={styles.infoPositiveIcon} aria-hidden="true" />
        <span>PlayStation 5</span>
      </div>
      <div style={styles.infoRow}>
        <FiCheckCircle style={styles.infoPositiveIcon} aria-hidden="true" />
        <span>Xbox Series X|S</span>
      </div>
      <div style={styles.infoRow}>
        <FiCheckCircle style={styles.infoPositiveIcon} aria-hidden="true" />
        <span>PC</span>
      </div>
      <div style={styles.infoRow}>
        <FiXCircle style={styles.infoMutedIcon} aria-hidden="true" />
        <span>PlayStation 4</span>
      </div>
      <div style={styles.infoRow}>
        <FiXCircle style={styles.infoMutedIcon} aria-hidden="true" />
        <span>Xbox One</span>
      </div>
    </div>
  </div>
</div>


<section style={styles.visionSection}>
  <h2 style={styles.visionTitle}>
    {t("landing.visionTitle")}
  </h2>

  <div style={styles.grid}>
    <div style={styles.card}>
      <h3 style={styles.cardTitle}>
        {t("landing.visionPoint1Title")}
      </h3>
      <p style={styles.cardText}>
        {t("landing.visionPoint1Text")}
      </p>
    </div>

    <div style={styles.card}>
      <h3 style={styles.cardTitle}>
        {t("landing.visionPoint2Title")}
      </h3>
      <p style={styles.cardText}>
        {t("landing.visionPoint2Text")}
      </p>
    </div>

    <div style={styles.card}>
      <h3 style={styles.cardTitle}>
        {t("landing.visionPoint3Title")}
      </h3>
      <p style={styles.cardText}>
        {t("landing.visionPoint3Text")}
      </p>
    </div>
    <div style={styles.card}>
  <h3 style={styles.cardTitle}>
    {t("landing.feedbackTitle")}
  </h3>
  <p style={styles.cardText}>
    {t("landing.feedbackText")}
  </p>
</div>
  </div>
</section>

      </section>

      {/* HOW IT WORKS */}
     <section id="how-it-works" style={styles.section}>
	<h2 style={styles.sectionTitle}>
	  {t("landing.howItWorksTitle")}
	</h2>

	  <div style={styles.flowWrapper}>

	    <div className="how-it-works-row" style={styles.flowRow}>
	      <div style={styles.flowText}>
	        <div
	          key={`${language}-${activeHowItWorksIndex}`}
	          className="how-it-works-copy"
	          style={styles.flowCopyAnimation}
	        >
	          <span style={styles.flowStepLabel}>
	            {String(activeHowItWorksIndex + 1).padStart(2, "0")} / {HOW_IT_WORKS_STEPS.length}
	          </span>
	          <h3 style={styles.flowTitle}>
	            {activeHowItWorksCopy.title}
	          </h3>
	          <p style={styles.flowDescription}>
	            {activeHowItWorksCopy.text}
	          </p>
	        </div>
	      </div>

	      <div className="how-it-works-video-shell" style={styles.videoShell}>
	        <video
	          src="/landing/how-it-works.mp4"
	          autoPlay
	          loop
	          muted
	          playsInline
	          preload="metadata"
	          style={styles.video}
	          onTimeUpdate={handleHowItWorksTimeUpdate}
	          onLoadedMetadata={handleHowItWorksLoopReset}
	          onSeeked={handleHowItWorksTimeUpdate}
	          onEnded={handleHowItWorksLoopReset}
	        />
	      </div>
	    </div>

	  </div>
	</section>

      {/* WHY */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>
  {t("landing.whyTitle")}
</h2>

        <div style={styles.grid}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>
  <TrophyIcon size={24} /> {t("landing.realCompetitionTitle")}
</h3>
            <p style={styles.cardText}>
  {t("landing.realCompetitionText")}
</p>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>
  ⚡ {t("landing.fastMatchesTitle")}
</h3>
           <p style={styles.cardText}>
  {t("landing.fastMatchesText")}
</p> 
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>
  <QuestsIcon size={26} /> {t("landing.skillTitle")}
</h3>
            <p style={styles.cardText}>
  {t("landing.skillText")}
</p>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>
  🎁 {t("landing.rewardsTitle")}
</h3>
            <p style={styles.cardText}>
  {t("landing.rewardsText")}
</p>
          </div>
        </div>
      </section>

      {/* TESTPHASE TEXT */}
      <section style={styles.infoSection}>
        <p style={styles.infoText}>
  {t("landing.betaText")}
</p>
      </section>
     <div style={styles.footer}>
  <a
    href="/impressum"
    style={styles.footerLink}
    onClick={(event) => {
      event.preventDefault();
      openLandingInfoPage("/impressum");
    }}
  >
    {t("landing.impressum")}
  </a>

  <a
    href="/datenschutz"
    style={styles.footerLink}
    onClick={(event) => {
      event.preventDefault();
      openLandingInfoPage("/datenschutz");
    }}
  >
    {t("landing.privacy")}
  </a>

  <a
    href="/agb"
    style={styles.footerLink}
    onClick={(event) => {
      event.preventDefault();
      openLandingInfoPage("/agb");
    }}
  >
    {t("landing.terms")}
  </a>

  <a
    href="/contact"
    style={styles.footerLink}
    onClick={(event) => {
      event.preventDefault();
      openLandingInfoPage("/contact");
    }}
  >
  {t("landing.contact")}
</a>

  <a
    href="/faqs"
    style={styles.footerLink}
    onClick={(event) => {
      event.preventDefault();
      openLandingInfoPage("/faqs");
    }}
  >
    {t("landing.faqs")}
  </a>
</div>
      <CopyrightFooter variant="landing" />
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100dvh",
    position: "relative",
    overflowX: "hidden",
    fontFamily: "Arial, sans-serif",
    color: "#ffffff",
    background: LANDING_BACKGROUND,
    backgroundColor: "#0f0418",
    boxSizing: "border-box",
  },
  pageGlow: {
    position: "absolute",
    top: "-190px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "760px",
    height: "620px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(168,85,247,0.22) 0%, rgba(168,85,247,0.10) 34%, rgba(168,85,247,0.00) 72%)",
    pointerEvents: "none",
  },
  languageSwitch: {
    position: "relative",
    zIndex: 50,
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "calc(20px + var(--vq-safe-area-top)) calc(20px + var(--vq-safe-area-right)) 0 calc(20px + var(--vq-safe-area-left))",
  },

languageBtn: {
  minWidth: "52px",
  padding: "10px 14px",
  fontSize: "14px",
  fontWeight: "700",
  borderRadius: "999px",
  border: "1px solid rgba(255,255,255,0.16)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  cursor: "pointer",
  transition: "all 0.2s ease",
  backdropFilter: "blur(6px)",
},

languageBtnActive: {
  background: "linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)",
  border: "1px solid rgba(168,85,247,0.65)",
  boxShadow: "0 8px 20px rgba(168,85,247,0.28)",
  transform: "translateY(-1px)",
},

  hero: {
    position: "relative",
    overflow: "visible",
    padding: "120px 20px 90px",
    textAlign: "center",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  heroGlow: {
    position: "absolute",
    top: "-180px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "700px",
    height: "700px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(168,85,247,0.22) 0%, rgba(168,85,247,0.10) 30%, rgba(168,85,247,0.00) 70%)",
    pointerEvents: "none",
  },
  heroLogo: {
    position: "relative",
    width: "min(150px, 34vw)",
    height: "auto",
    display: "block",
    margin: "0 auto 22px",
    filter: "drop-shadow(0 18px 34px rgba(168,85,247,0.35))",
  },
  title: {
    position: "relative",
    fontSize: "56px",
    fontWeight: "800",
    lineHeight: "1.1",
    marginBottom: "22px",
    maxWidth: "1000px",
    marginLeft: "auto",
    marginRight: "auto",
    letterSpacing: "-1px",
  },
  heroClaim: {
    position: "relative",
    maxWidth: "760px",
    margin: "0 auto 20px",
    color: "#ffffff",
    fontSize: "20px",
    fontWeight: "500",
    letterSpacing: "0.2px",
    lineHeight: "1.35",
    textTransform: "uppercase",
  },
  subtitle: {
    position: "relative",
    fontSize: "22px",
    lineHeight: "1.5",
    color: "rgba(255,255,255,0.82)",
    maxWidth: "900px",
    margin: "0 auto 34px",
  },
  buttons: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  primaryBtn: {
    padding: "14px 28px",
    fontSize: "17px",
    fontWeight: "700",
    background: "linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    boxShadow: "0 10px 30px rgba(168,85,247,0.30)",
  },
  secondaryBtn: {
    padding: "14px 28px",
    fontSize: "17px",
    fontWeight: "600",
    backgroundColor: "rgba(255,255,255,0.04)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: "12px",
    cursor: "pointer",
    backdropFilter: "blur(6px)",
  },
  section: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "70px 20px",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: "44px",
    fontWeight: "800",
    marginBottom: "34px",
    letterSpacing: "-0.5px",
  },
	  flowWrapper: {
	  display: "flex",
	  flexDirection: "column",
	  gap: "24px",
},

flowRow: {
  display: "grid",
  gridTemplateColumns: "minmax(300px, 0.95fr) minmax(280px, 1.05fr)",
  alignItems: "center",
  gap: "34px",
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.065), rgba(168,85,247,0.065))",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "24px",
  padding: "30px",
  boxShadow: "0 24px 70px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08)",
  overflow: "hidden",
},

flowText: {
  textAlign: "left",
  minHeight: "260px",
  display: "flex",
  alignItems: "center",
},

flowCopyAnimation: {
  minHeight: "235px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
},

flowStepLabel: {
  display: "inline-flex",
  width: "fit-content",
  marginBottom: "18px",
  padding: "7px 12px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "rgba(255,255,255,0.68)",
  fontSize: "13px",
  fontWeight: "800",
  letterSpacing: "1.2px",
},

flowTitle: {
  fontSize: "34px",
  fontWeight: "800",
  margin: "0 0 14px",
  letterSpacing: "-0.4px",
},

flowDescription: {
  fontSize: "18px",
  lineHeight: "1.6",
  color: "rgba(255,255,255,0.82)",
  whiteSpace: "pre-line",
  margin: 0,
},
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "22px",
    marginTop: "24px",
  },
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(168,85,247,0.18)",
    borderRadius: "18px",
    padding: "28px 22px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
  },
  cardTitle: {
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "14px",
  },
  cardText: {
    fontSize: "20px",
    lineHeight: "1.55",
    color: "rgba(255,255,255,0.82)",
    margin: 0,
  },
  infoSection: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "30px 20px 70px",
    textAlign: "center",
  },
 videoShell: {
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
},

 video: {
  width: "min(100%, 430px)",
  maxWidth: "100%",
  height: "auto",
  borderRadius: "22px",
  border: "1px solid rgba(255,255,255,0.16)",
  boxShadow: "0 20px 55px rgba(0,0,0,0.46)",
  background: "rgba(0,0,0,0.25)",
  display: "block",
},
footer: {
  display: "flex",
  justifyContent: "center",
  gap: "30px",
  padding: "30px calc(30px + var(--vq-safe-area-right)) 16px calc(30px + var(--vq-safe-area-left))",
  borderTop: "1px solid rgba(255,255,255,0.1)",
  marginTop: "50px",
  flexWrap: "wrap",
},

socialRow: {
  marginTop: "22px",
  display: "flex",
  justifyContent: "center",
  gap: "14px",
  flexWrap: "wrap",
},

socialIconBtn: {
  width: "58px",
  height: "58px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  textDecoration: "none",
  fontSize: "34px",
  background: "transparent",
  border: "none",
  boxShadow: "none",
},

infoGrid: {
  position: "relative",
  margin: "34px auto 0",
  maxWidth: "820px",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gap: "46px",
  alignItems: "start",
},

infoGroup: {
  minWidth: 0,
  textAlign: "left",
},

infoHeading: {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  color: "#ffffff",
  fontSize: "14px",
  fontWeight: "800",
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  marginBottom: "14px",
},

infoRows: {
  display: "grid",
  gap: "9px",
},

infoRow: {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  color: "rgba(255,255,255,0.9)",
  fontSize: "16px",
  fontWeight: "600",
  lineHeight: "1.35",
},

infoPositiveIcon: {
  flex: "0 0 auto",
  color: "#4ade80",
  fontSize: "18px",
},

infoNeutralIcon: {
  flex: "0 0 auto",
  color: "rgba(255,255,255,0.58)",
  fontSize: "18px",
},

infoMutedIcon: {
  flex: "0 0 auto",
  color: "rgba(255,255,255,0.36)",
  fontSize: "18px",
},

infoFinePrint: {
  margin: "14px 0 0",
  color: "rgba(255,255,255,0.62)",
  fontSize: "13px",
  lineHeight: "1.5",
},

visionSection: {
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "40px 20px 20px",
  textAlign: "center",
},

visionTitle: {
  fontSize: "36px",
  fontWeight: "800",
  marginBottom: "28px",
},

footerLink: {
  color: "rgba(255,255,255,0.8)",
  textDecoration: "none",
  fontSize: "15px",
},
  infoText: {
    fontSize: "20px",
    lineHeight: "1.6",
    color: "rgba(255,255,255,0.78)",
    margin: 0,
  },

  
};
